import type {
  ModelProfileResponse,
  ShowCaptionEvent,
  ShowSummary,
  StreamSignal,
  StreamSignalOutgoing,
} from '@fetmode/types';
import { resolveApiUrl, useAuth } from '@fetmode/app-core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from 'react';

import { useShowRealtime } from '../live/useShowRealtime.js';

const apiBaseUrl = resolveApiUrl(import.meta.env);

export interface UseStudioModelResult {
  profile: ModelProfileResponse | null;
  liveShow: ShowSummary | null;
  /** Any active session including break — keep studio session tools aware. */
  isStreaming: boolean;
  /** Hard live / waiting only — locks nav away from other studio pages. */
  isNavLocked: boolean;
  isLoading: boolean;
  error: string | null;
  /** Bumps when private queue / tip-menu / jukebox board changes. */
  requestsRevision: number;
  bumpRequestsRevision: () => void;
  refresh: (options?: { quiet?: boolean }) => Promise<void>;
  setProfile: Dispatch<SetStateAction<ModelProfileResponse | null>>;
  setLiveShow: Dispatch<SetStateAction<ShowSummary | null>>;
  sendStreamSignal: (payload: StreamSignalOutgoing) => void;
  sendShowCaption: (payload: ShowCaptionEvent) => void;
  streamPeerId: string | null;
  subscribeStreamSignals: (handler: (signal: StreamSignal) => void) => () => void;
}

const StudioModelContext = createContext<UseStudioModelResult | null>(null);

export function StudioModelProvider({ children }: { children: ReactNode }): ReactElement {
  const { api, user } = useAuth();
  const [profile, setProfile] = useState<ModelProfileResponse | null>(null);
  const [liveShow, setLiveShow] = useState<ShowSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestsRevision, setRequestsRevision] = useState(0);

  const refresh = useCallback(async (options?: { quiet?: boolean }) => {
    if (!user) {
      setProfile(null);
      setLiveShow(null);
      setIsLoading(false);
      return;
    }

    if (!options?.quiet) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const result = await api.getModelProfile();
      setProfile(result.profile);
      setLiveShow(result.liveShow);
    } catch {
      setError('Unable to load studio profile.');
      setProfile(null);
      setLiveShow(null);
    } finally {
      if (!options?.quiet) {
        setIsLoading(false);
      }
    }
  }, [api, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const bumpRequestsRevision = useCallback(() => {
    setRequestsRevision((current) => current + 1);
  }, []);

  const streamListenersRef = useRef(new Set<(signal: StreamSignal) => void>());
  const subscribeStreamSignals = useCallback((handler: (signal: StreamSignal) => void) => {
    streamListenersRef.current.add(handler);
    return () => {
      streamListenersRef.current.delete(handler);
    };
  }, []);

  const { sendStreamSignal, sendShowCaption, localPeerId: streamPeerId } = useShowRealtime(
    apiBaseUrl,
    {
      username: profile?.username ?? null,
      showId: liveShow?.id ?? null,
    },
    {
      onShowUpdated: (event) => {
        setLiveShow((current) => {
          if (event.show.status === 'ENDED') {
            return current && current.id === event.show.id ? null : current;
          }
          if (current && current.id !== event.show.id) {
            return event.show;
          }
          return event.show;
        });
        void refresh({ quiet: true });
      },
      onShowEnded: () => {
        void refresh({ quiet: true });
      },
      onShowRequestsChanged: () => {
        bumpRequestsRevision();
      },
      onStreamSignal: (signal) => {
        for (const listener of streamListenersRef.current) {
          listener(signal);
        }
      },
    },
  );

  const value = useMemo<UseStudioModelResult>(
    () => {
      const hasSession = liveShow !== null;
      const isHardLive =
        liveShow !== null &&
        (liveShow.status === 'LIVE' || liveShow.status === 'WAITING');

      return {
        profile,
        liveShow,
        isStreaming:
          hasSession &&
          (liveShow.status === 'LIVE' ||
            liveShow.status === 'WAITING' ||
            liveShow.status === 'PAUSED' ||
            liveShow.status === 'AWAY'),
        isNavLocked: isHardLive,
        isLoading,
        error,
        requestsRevision,
        bumpRequestsRevision,
        refresh,
        setProfile,
        setLiveShow,
        sendStreamSignal,
        sendShowCaption,
        streamPeerId,
        subscribeStreamSignals,
      };
    },
    [
      profile,
      liveShow,
      isLoading,
      error,
      requestsRevision,
      bumpRequestsRevision,
      refresh,
      sendStreamSignal,
      sendShowCaption,
      streamPeerId,
      subscribeStreamSignals,
    ],
  );

  return <StudioModelContext.Provider value={value}>{children}</StudioModelContext.Provider>;
}

export function useStudioModel(): UseStudioModelResult {
  const context = useContext(StudioModelContext);
  if (!context) {
    throw new Error('useStudioModel must be used within StudioModelProvider');
  }
  return context;
}
